/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import DataTable from 'react-data-table-component';
import { Pagination } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import Api from '../../../services/Api';
import ContentLoader from '../../atoms/ContentLoader/ContentLoader';
import FilterInput from '../../atoms/FilterInput/FilterInput';
import TableActions from '../../molecules/TableActions/TableActions';
import { downloadCSV } from '../../../helpers/csv';
import './ReactiveTable.scss';
// import Modal from '../Modal/Modal';
import ModalBase from '../../Modals/ModalBase/ModalBase';

const ReactiveTable = ({
  columns,
  data,
  expandedComponent,
  isLoading,
  selectableRowDisabledCallback,
  endpoint,
  isDense,
  searchable,
  title,
  exportableKeys,
  modalTitle,
  fields,
  onFetching,
  extraParameters,
}) => {
  const [rows, setRows] = useState(data || []);
  const [filteredData, setFilteredData] = useState(data);
  const [tableColumns, setTableColumns] = useState(columns);
  const [loading, setLoading] = useState(isLoading);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(15);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(undefined);
  const [resetPagination, setResetPagination] = useState(false);
  const [isModalShown, setIsModalShown] = useState(false);
  const [selectedItem, setSelectedItem] = useState(undefined);
  const fetchData = () => {
    if (!endpoint) {
      const filtered = data.filter((row) => {
        const objKeys = Object.keys(row);
        let include = search === undefined;
        objKeys.forEach((key) => {
          if (!row[key]) {
            return;
          }
          const matchFound = row[key].toString().startsWith(search);
          if (matchFound) {
            include = true;
          }
        });
        return include;
      });
      setFilteredData(filtered);
      return;
    }
    setLoading(true);
    let query = `?per_page=${perPage}&page=${page}`;
    if (search) {
      query += `&search=${encodeURI(search)}`;
    }
    if (extraParameters) {
      extraParameters.forEach((item) => {
        Object.keys(item).forEach((key) => {
          query += `&${key}=${item[key]}`;
        });
      });
    }
    Api.get(endpoint + query).then((result) => {
      if (result.status === 200) {
        setRows(result.data.data);
        setTotalRows(result.data.total);
        if (onFetching) {
          onFetching();
        }
      }
      setLoading(false);
    });
  };
  const needToAddActions = tableColumns.some(column => column.cell !== undefined);
  if (!needToAddActions) {
    setTableColumns([
      ...tableColumns,
      {
        cell: row => (
          <TableActions
            onEdit={() => {
              window.scrollTo(0, 0);
              window.scrollTop = 0;
              setIsModalShown(true);
              setSelectedItem(row);
            }}
            onDelete={() => {
              // eslint-disable-next-line no-alert
              const confirmed = window.confirm(`Are you sure you want to delete ${row.name}?`);
              if (!confirmed) {
                return;
              }
              Api.delete(`${endpoint}/${row.id}`).then(({ status }) => {
                if (status === 204) {
                  fetchData();
                }
              });
            }}
          />
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ]);
  }

  useEffect(() => {
    if (loading) {
      return;
    }
    fetchData();
  }, [perPage, page, search]);
  const filter = useMemo(() => {
    const reset = () => {
      setSearch(undefined);
      setFilteredData(undefined);
      setResetPagination(!resetPagination);
    };
    return (
      <div className="table-subheader">
        <div className="table-actions">
          <button
            className="table-action-button"
            type="button"
            onClick={() => {
              setSelectedItem(undefined);
              setIsModalShown(true);
            }}
          >
            <FontAwesomeIcon icon={solid('plus')} color="#fff" />
          </button>
          <button
            className="table-action-button"
            type="button"
            onClick={() => fetchData()}
          >
            <FontAwesomeIcon icon={solid('refresh')} color="#fff" />
          </button>
          <button
            className="table-action-button"
            type="button"
            onClick={() => {
              if (rows.length === 0) {
                return;
              }
              let keys = [];
              if (exportableKeys) {
                keys = exportableKeys;
              } else {
                keys = Object.keys(rows[0]);
              }

              downloadCSV(rows, keys);
            }}
          >
            <FontAwesomeIcon icon={solid('cloud-download')} color="#fff" />
          </button>
        </div>
        <FilterInput onTriggered={value => setSearch(value)} onDismiss={() => reset()} />
      </div>
    );
  }, [search]);
  const customStyles = {
    headRow: {
      style: {
        border: 'none',
      },
    },
    headCells: {
      style: {
        color: '#202124',
        fontSize: '14px',
      },
    },
    rows: {
      highlightOnHoverStyle: {
        backgroundColor: 'rgb(230, 244, 244)',
        borderBottomColor: '#FFFFFF',
        borderRadius: '25px',
        outline: '1px solid #FFFFFF',
      },
    },
    pagination: {
      style: {
        border: 'none',
      },
    },
  };
  const popupTitle = selectedItem ? `Edit ${modalTitle}` : `Create ${modalTitle}`;
  return (
    <>
      <Modal
        onClosed={() => setIsModalShown(false)}
        isOpen={isModalShown}
        isShown
        size="lg"
        toggle={() => setIsModalShown(false)}
      >
        <ModalHeader
          toggle={() => setIsModalShown(false)}
        >
          {popupTitle}
        </ModalHeader>
        <ModalBody>
          <ModalBase
            editableItem={selectedItem}
            endpoint={endpoint}
            fields={fields}
            onRequestClose={() => setIsModalShown(false)}
            afterEdit={() => fetchData()}
            afterCreate={() => fetchData()}
            extraParameters={extraParameters}
          />
        </ModalBody>
      </Modal>
      <DataTable
        columns={tableColumns}
        data={filteredData || rows}
        title={title}
        progressPending={loading}
        progressComponent={<ContentLoader />}
        paginationTotalRows={endpoint ? totalRows : undefined}
        customStyles={customStyles}
        expandableRowsComponent={expandedComponent}
        subHeaderComponent={searchable ? filter : undefined}
        dense={isDense}
        onChangeRowsPerPage={endpoint ? number => setPerPage(number) : undefined}
        onChangePage={(number) => {
          if (endpoint) {
            setPage(number);
          }
        }}
        selectableRowDisabled={(row) => {
          if (selectableRowDisabledCallback) {
            selectableRowDisabledCallback(row);
          }
        }}
        expandableRows={expandedComponent !== undefined}
        highlightOnHover
        pointerOnHover
        persistTableHead
        subHeader={searchable}
      />
      <div className="pagination-container">
        <div className="select-container">
          <span className="me-2">Rows per page</span>
          <select className="d-inline" onChange={ev => setPerPage(ev.target.value)}>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path d="M7 10l5 5 5-5z" />
            <path d="M0 0h24v24H0z" fill="none" />
          </svg>
        </div>
        <div className="position-container">
          <span>{((perPage * page) - perPage) + 1}</span>
          <span>-</span>
          <span>{((perPage * (page + 1)) - perPage)}</span>
          <span>
            {' of '}
            {totalRows}
          </span>
        </div>
        <Pagination>
          <Pagination.First disabled={page === 1} onClick={() => setPage(1)} />
          <Pagination.Prev
            disabled={page === 1}
            onClick={() => setPage(prevState => prevState - 1)}
          />
          <Pagination.Next onClick={() => setPage(prevState => prevState + 1)} />
          <Pagination.Last onClick={() => setPage(Math.ceil(totalRows / perPage))} />
        </Pagination>
      </div>
    </>
  );
};

ReactiveTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  expandedComponent: PropTypes.func,
  isLoading: PropTypes.bool,
  selectableRowDisabledCallback: PropTypes.func,
  endpoint: PropTypes.string,
  isDense: PropTypes.bool,
  searchable: PropTypes.bool,
  title: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  exportableKeys: PropTypes.array,
  modalTitle: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  fields: PropTypes.array,
  onFetching: PropTypes.func,
  extraParameters: PropTypes.array,
};

ReactiveTable.defaultProps = {
  expandedComponent: undefined,
  isLoading: false,
  selectableRowDisabledCallback: undefined,
  endpoint: undefined,
  isDense: false,
  searchable: true,
  title: undefined,
  exportableKeys: undefined,
  modalTitle: '',
  fields: [],
  onFetching: undefined,
  extraParameters: undefined,
};

export default ReactiveTable;
